import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Loader2, ExternalLink, Trash2, Upload, Plus, X, Check, FileText, PackageCheck, Wrench, Lock } from "lucide-react";
import { money, locationScore } from "@/lib/helpers";
import LocationMap from "@/components/LocationMap";
import DriveImport from "@/components/DriveImport";
import { useAuth } from "@/context/AuthContext";

const STATUSES = [
  { value: "potential", label: "Potential" },
  { value: "rejected", label: "Rejected" },
  { value: "approved", label: "Approved" },
  { value: "agreed", label: "Agreed" },
  { value: "in_progress", label: "In Progress" },
  { value: "live", label: "Live" },
];
const NO_CAT = "__none__";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

function Section({ title, children, action }) {
  return (
    <div className="border border-zinc-200 bg-white">
      <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-2.5">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-700">{title}</h3>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function LocationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [loc, setLoc] = useState(null);
  const [elements, setElements] = useState([]);
  const [products, setProducts] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [note, setNote] = useState("");
  const [cats, setCats] = useState([]);
  const [tl, setTl] = useState({ title: "", date: "" });
  const mediaRef = useRef();
  const docRef = useRef();

  const load = useCallback(async () => {
    const [l, el, cats, p, e] = await Promise.all([
      api.get(`/locations/${id}`), api.get("/scorecard-elements"), api.get("/location-categories"),
      api.get("/products"), api.get("/equipment"),
    ]);
    setLoc(l.data); setElements(el.data); setProducts(p.data); setEquipment(e.data); setCats(cats.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!loc) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#b32233]" /></div>;

  const reloadLoc = async () => { const { data } = await api.get(`/locations/${id}`); setLoc(data); };
  const patch = async (body) => {
    try { const { data } = await api.patch(`/locations/${id}`, body); setLoc(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { const { data } = await api.post(`/locations/${id}/notes`, { message: note }); setLoc(data); setNote(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upload = async (ev, kind) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/locations/${id}/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setLoc(data); toast.success("Uploaded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { if (kind === "media" && mediaRef.current) mediaRef.current.value = ""; if (kind === "documents" && docRef.current) docRef.current.value = ""; }
  };
  const delFile = async (kind, fid) => {
    try { const { data } = await api.delete(`/locations/${id}/${kind}/${fid}`); setLoc(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setScore = async (elId, val) => {
    const scores = { ...(loc.scores || {}), [elId]: val };
    try { const { data } = await api.put(`/locations/${id}/scores`, { scores }); setLoc(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addTimeline = async () => {
    if (!tl.title.trim()) return;
    try { const { data } = await api.post(`/locations/${id}/timeline`, tl); setLoc(data); setTl({ title: "", date: "" }); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleTimeline = async (t) => {
    try { const { data } = await api.patch(`/locations/${id}/timeline/${t.id}`, { done: !t.done }); setLoc(data); } catch (e) {}
  };
  const delTimeline = async (tid) => {
    try { const { data } = await api.delete(`/locations/${id}/timeline/${tid}`); setLoc(data); } catch (e) {}
  };
  const addProduct = async (pid) => {
    try { await api.post(`/locations/${id}/products`, { product_id: pid }); await reloadLoc(); toast.success("Product added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeProduct = async (pid) => {
    try { await api.delete(`/locations/${id}/products/${pid}`); await reloadLoc(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addEquipment = async (eid) => {
    try { await api.post(`/locations/${id}/equipment`, { equipment_id: eid }); await reloadLoc(); toast.success("Equipment added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeEquipment = async (eid) => {
    try { await api.delete(`/locations/${id}/equipment/${eid}`); await reloadLoc(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const { score, hasScore } = locationScore(loc, elements);
  const totalSqft = (Number(loc.foh_sqft) || 0) + (Number(loc.boh_sqft) || 0);
  const status = loc.status || "potential";
  const unlocked = ["agreed", "in_progress", "live"].includes(status);
  const docsUnlocked = ["approved", "agreed", "in_progress", "live"].includes(status);

  const approved = products.filter((p) => p.tag === "approved");
  const inLoc = (loc.product_ids || []).map((pid) => products.find((p) => p.id === pid)).filter(Boolean);
  const availableProducts = approved.filter((p) => !(loc.product_ids || []).includes(p.id));
  const totalValue = inLoc.reduce((s, p) => s + (Number(p.rrp) || 0), 0);
  const inEq = (loc.equipment_ids || []).map((eid) => equipment.find((e) => e.id === eid)).filter(Boolean);
  const availableEq = equipment.filter((e) => !(loc.equipment_ids || []).includes(e.id));

  return (
    <div className="p-6" data-testid="location-detail">
      <button onClick={() => navigate("/locations")} className="mb-4 flex items-center gap-1 text-sm text-zinc-500 hover:text-[#b32233]"><ArrowLeft className="h-4 w-4" /> Back to Locations</button>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <input value={loc.name} onChange={(e) => setLoc({ ...loc, name: e.target.value })} onBlur={(e) => patch({ name: e.target.value })} data-testid="detail-name" className="font-heading text-2xl font-bold tracking-tight text-zinc-900 outline-none focus:border-b focus:border-[#b32233]" />
          {loc.address && <a href={loc.maps_url || "#"} target="_blank" rel="noreferrer" className="mt-1 flex items-center gap-1 text-xs text-[#b32233] hover:underline">{loc.address} <ExternalLink className="h-3 w-3" /></a>}
        </div>
        <Select value={status} onValueChange={(v) => patch({ status: v })}>
          <SelectTrigger data-testid="detail-status" className="h-9 w-40 rounded-none"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-none bg-transparent p-0">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-none border border-zinc-200 data-[state=active]:border-[#b32233] data-[state=active]:bg-[#b32233] data-[state=active]:text-white">Overview</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products" className="rounded-none border border-zinc-200 data-[state=active]:border-[#b32233] data-[state=active]:bg-[#b32233] data-[state=active]:text-white">Products</TabsTrigger>
          <TabsTrigger value="equipment" data-testid="tab-equipment" className="rounded-none border border-zinc-200 data-[state=active]:border-[#b32233] data-[state=active]:bg-[#b32233] data-[state=active]:text-white">Equipment</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-none border border-zinc-200 data-[state=active]:border-[#b32233] data-[state=active]:bg-[#b32233] data-[state=active]:text-white">Documents</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes" className="rounded-none border border-zinc-200 data-[state=active]:border-[#b32233] data-[state=active]:bg-[#b32233] data-[state=active]:text-white">Notes</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-5">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Section title="Location">
              <LocationMap lat={loc.lat} lng={loc.lng} />
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <label className="block"><span className="text-xs uppercase tracking-wider text-zinc-500">Front of House (sq ft)</span>
                  <input type="number" data-testid="detail-foh" defaultValue={loc.foh_sqft ?? ""} onBlur={(e) => patch({ foh_sqft: e.target.value === "" ? null : parseFloat(e.target.value) })} className="mt-1 w-full border border-zinc-200 px-2 py-1.5 outline-none focus:border-[#b32233]" /></label>
                <label className="block"><span className="text-xs uppercase tracking-wider text-zinc-500">Back of House (sq ft)</span>
                  <input type="number" data-testid="detail-boh" defaultValue={loc.boh_sqft ?? ""} onBlur={(e) => patch({ boh_sqft: e.target.value === "" ? null : parseFloat(e.target.value) })} className="mt-1 w-full border border-zinc-200 px-2 py-1.5 outline-none focus:border-[#b32233]" /></label>
                <div><span className="text-xs uppercase tracking-wider text-zinc-500">Total sq ft</span><p className="mt-1 py-1.5 font-semibold text-zinc-900" data-testid="detail-total-sqft">{totalSqft.toLocaleString()}</p></div>
                <label className="block"><span className="text-xs uppercase tracking-wider text-zinc-500">Rent</span>
                  <input type="number" data-testid="detail-rent" defaultValue={loc.rent ?? ""} onBlur={(e) => patch({ rent: e.target.value === "" ? null : parseFloat(e.target.value) })} className="mt-1 w-full border border-zinc-200 px-2 py-1.5 outline-none focus:border-[#b32233]" /></label>
                <label className="block"><span className="text-xs uppercase tracking-wider text-zinc-500">Category</span>
                  <Select value={loc.category_id || NO_CAT} onValueChange={(v) => patch({ category_id: v === NO_CAT ? null : v })}>
                    <SelectTrigger data-testid="detail-category" className="mt-1 rounded-none"><SelectValue placeholder="No category" /></SelectTrigger>
                    <SelectContent><SelectItem value={NO_CAT}>No category</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select></label>
                {hasScore && <div><span className="text-xs uppercase tracking-wider text-zinc-500">Score</span><p className="mt-1 py-1.5 font-heading text-lg font-bold text-[#b32233]" data-testid="detail-score">{score.toFixed(1)} / 10</p></div>}
              </div>
            </Section>

            {status === "potential" && (
              <Section title="Scorecard" action={hasScore && <span className="font-heading text-base font-bold text-[#b32233]" data-testid="scorecard-overall">{score.toFixed(1)} / 10</span>}>
                {elements.length === 0 ? <p className="text-xs text-zinc-400">No scorecard elements yet. Add them in Settings → Locations.</p> : (
                  <div className="space-y-2">
                    <p className="text-xs text-zinc-500">Score each element from 0–10 (10 = highest).</p>
                    {elements.map((el) => (
                      <div key={el.id} className="flex items-center justify-between gap-3" data-testid={`score-row-${el.id}`}>
                        <span className="flex-1 text-sm text-zinc-700">{el.name}</span>
                        <input type="number" min="0" max="10" step="1" data-testid={`score-input-${el.id}`} value={loc.scores?.[el.id] ?? ""} onChange={(e) => setScore(el.id, e.target.value === "" ? null : Math.min(10, Math.max(0, parseFloat(e.target.value))))} className="w-16 border border-zinc-200 px-2 py-1 text-sm outline-none focus:border-[#b32233]" />
                      </div>
                    ))}
                    <div className="mt-2 border-t border-zinc-200 pt-2 text-right font-heading text-lg font-bold text-[#b32233]" data-testid="scorecard-total">{score.toFixed(1)} / 10</div>
                  </div>
                )}
              </Section>
            )}

            <Section title="Media" action={canManage && (
              <div className="flex items-center gap-3">
                <DriveImport targetType="location_media" targetId={id} onImported={(parent) => parent && setLoc(parent)} />
                <button onClick={() => mediaRef.current?.click()} data-testid="upload-media" className="flex items-center gap-1 text-xs font-medium text-[#b32233] hover:underline"><Upload className="h-3 w-3" /> Upload</button>
              </div>
            )}>
              <input ref={mediaRef} type="file" accept="image/*,video/*" className="hidden" onChange={(e) => upload(e, "media")} />
              {(loc.media || []).length === 0 ? <p className="text-xs text-zinc-400">No media uploaded</p> : (
                <div className="grid grid-cols-3 gap-2">
                  {loc.media.map((m) => (
                    <div key={m.id} className="group relative aspect-square overflow-hidden border border-zinc-200 bg-zinc-50" data-testid={`media-${m.id}`}>
                      {m.is_image ? <img src={`${API}/files/${m.storage_path}`} alt={m.original_filename} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center p-1 text-center text-[10px] text-zinc-500">{m.original_filename}</div>}
                      {canManage && <button onClick={() => delFile("media", m.id)} className="absolute right-1 top-1 bg-white/80 p-0.5 text-zinc-500 opacity-0 group-hover:opacity-100 hover:text-[#b32233]"><X className="h-3 w-3" /></button>}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {unlocked && (
              <Section title="Timeline">
                <div className="mb-3 flex gap-2">
                  <input value={tl.title} onChange={(e) => setTl({ ...tl, title: e.target.value })} placeholder="Milestone" data-testid="timeline-title" className="flex-1 border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#b32233]" />
                  <input type="date" value={tl.date} onChange={(e) => setTl({ ...tl, date: e.target.value })} data-testid="timeline-date" className="border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#b32233]" />
                  <button onClick={addTimeline} data-testid="timeline-add" className="bg-[#b32233] px-3 text-sm text-white hover:bg-[#8f1a28]"><Plus className="h-4 w-4" /></button>
                </div>
                {(loc.timeline || []).length === 0 ? <p className="text-xs text-zinc-400">No milestones yet</p> : (
                  <div className="space-y-1">
                    {loc.timeline.map((t) => (
                      <div key={t.id} className="flex items-center justify-between border border-zinc-100 px-2 py-1.5 text-sm" data-testid={`timeline-${t.id}`}>
                        <button onClick={() => toggleTimeline(t)} className="flex items-center gap-2">
                          <span className={`flex h-4 w-4 items-center justify-center border ${t.done ? "border-green-600 bg-green-600 text-white" : "border-zinc-300"}`}>{t.done && <Check className="h-3 w-3" />}</span>
                          <span className={t.done ? "text-zinc-400 line-through" : "text-zinc-800"}>{t.title}</span>
                        </button>
                        <div className="flex items-center gap-2 text-xs text-zinc-400">{t.date}<button onClick={() => delTimeline(t.id)} className="hover:text-[#b32233]"><Trash2 className="h-3.5 w-3.5" /></button></div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            )}
          </div>
        </TabsContent>

        {/* PRODUCTS */}
        <TabsContent value="products" className="mt-5">
          <div className="lg:max-w-2xl">
            <Section title={`Products — ${inLoc.length} · ${money(totalValue)} retail`}>
              {canManage && (
                <div className="mb-3">
                  {availableProducts.length === 0 ? (
                    <p className="text-xs text-zinc-400">No approved products available. Tag products as "Approved" first.</p>
                  ) : (
                    <Select onValueChange={(v) => addProduct(v)}>
                      <SelectTrigger data-testid="add-product-select" className="rounded-none"><SelectValue placeholder="Add an approved product…" /></SelectTrigger>
                      <SelectContent>{availableProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              )}
              {inLoc.length === 0 ? (
                <p className="flex items-center gap-1.5 text-xs text-zinc-400"><PackageCheck className="h-3.5 w-3.5" /> No products stocked here yet</p>
              ) : (
                <div className="space-y-1">
                  {inLoc.map((p) => (
                    <div key={p.id} className="flex items-center justify-between border border-zinc-100 bg-zinc-50/50 px-2 py-1.5 text-sm" data-testid={`location-product-${p.id}`}>
                      <span className="text-zinc-800">{p.name}{p.shelf ? <span className="ml-2 text-xs text-zinc-400">Shelf {p.shelf}</span> : null}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">{money(p.rrp)}</span>
                        <button onClick={() => removeProduct(p.id)} data-testid={`remove-product-${p.id}`} className="text-zinc-300 hover:text-[#b32233]"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </TabsContent>

        {/* EQUIPMENT */}
        <TabsContent value="equipment" className="mt-5">
          <div className="lg:max-w-2xl">
            <Section title={`Equipment — ${inEq.length}`}>
              {canManage && (
                <div className="mb-3">
                  {availableEq.length === 0 ? (
                    <p className="text-xs text-zinc-400">No equipment available to add. Create equipment first.</p>
                  ) : (
                    <Select onValueChange={(v) => addEquipment(v)}>
                      <SelectTrigger data-testid="add-equipment-select" className="rounded-none"><SelectValue placeholder="Add equipment…" /></SelectTrigger>
                      <SelectContent>{availableEq.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
              )}
              {inEq.length === 0 ? (
                <p className="flex items-center gap-1.5 text-xs text-zinc-400"><Wrench className="h-3.5 w-3.5" /> No equipment here yet</p>
              ) : (
                <div className="space-y-1">
                  {inEq.map((e) => (
                    <div key={e.id} className="flex items-center justify-between border border-zinc-100 bg-zinc-50/50 px-2 py-1.5 text-sm" data-testid={`location-equipment-${e.id}`}>
                      <span className="text-zinc-800">{e.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500">{money(e.purchase_cost)}</span>
                        <button onClick={() => removeEquipment(e.id)} data-testid={`remove-equipment-${e.id}`} className="text-zinc-300 hover:text-[#b32233]"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="mt-5">
          <div className="lg:max-w-2xl">
            {docsUnlocked ? (
              <Section title="Documents" action={canManage && (
                <div className="flex items-center gap-3">
                  <DriveImport targetType="location_document" targetId={id} onImported={(parent) => parent && setLoc(parent)} />
                  <button onClick={() => docRef.current?.click()} data-testid="upload-document" className="flex items-center gap-1 text-xs font-medium text-[#b32233] hover:underline"><Upload className="h-3 w-3" /> Upload</button>
                </div>
              )}>
                <input ref={docRef} type="file" className="hidden" onChange={(e) => upload(e, "documents")} />
                {(loc.documents || []).length === 0 ? <p className="text-xs text-zinc-400">No documents (LOI, lease, estimates…)</p> : (
                  <div className="space-y-1">
                    {loc.documents.map((d) => (
                      <div key={d.id} className="flex items-center justify-between border border-zinc-100 bg-zinc-50/50 px-2 py-1.5 text-sm" data-testid={`doc-${d.id}`}>
                        <a href={`${API}/files/${d.storage_path}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-zinc-700 hover:text-[#b32233]"><FileText className="h-3.5 w-3.5" /> {d.original_filename}</a>
                        {canManage && <button onClick={() => delFile("documents", d.id)} className="text-zinc-300 hover:text-[#b32233]"><X className="h-3.5 w-3.5" /></button>}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            ) : (
              <div className="flex items-center justify-center gap-2 border border-dashed border-zinc-200 p-10 text-sm text-zinc-400" data-testid="documents-locked">
                <Lock className="h-4 w-4" /> Documents unlock once this location is Approved or later.
              </div>
            )}
          </div>
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="mt-5">
          <div className="lg:max-w-2xl">
            <Section title="Notes">
              <div className="mb-3 flex gap-2">
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note…" data-testid="note-input" className="flex-1 resize-none border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#b32233]" />
                <button onClick={addNote} data-testid="note-add" className="bg-[#b32233] px-3 text-sm text-white hover:bg-[#8f1a28]"><Plus className="h-4 w-4" /></button>
              </div>
              {(loc.notes || []).length === 0 ? <p className="text-xs text-zinc-400">No notes yet</p> : (
                <div className="space-y-2">
                  {[...loc.notes].reverse().map((n) => (
                    <div key={n.id} className="border-l-2 border-[#b32233] bg-zinc-50/50 px-3 py-1.5" data-testid={`note-${n.id}`}>
                      <p className="whitespace-pre-wrap text-sm text-zinc-700">{n.message}</p>
                      <p className="mt-0.5 text-[11px] text-zinc-400">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
